/**
 * Live alert feed: what arrived over the event stream during this session.
 *
 * This is a *view* of the last few firings, not the record — the record is
 * `frontend.sqlite`, read through `GET /api/alerts`. It exists so a tab
 * that is already open can show a firing the instant it happens, without
 * waiting for a refetch, and so the bell has an unread count between
 * queries.
 */
import { create } from "zustand";
import type { AlertRow } from "@/api/types";

/** Toasts kept on screen at once; older ones fall off the bottom. */
const MAX_TOASTS = 4;

interface AlertFeed {
  /** Newest first, capped. */
  toasts: AlertRow[];
  /** Unread count as of the last frame or query. */
  unread: number;
  /** Alerts seen this session, for "N since you opened the page". */
  session: number;
  arrived: (alert: AlertRow, unread: number) => void;
  dismiss: (id: number) => void;
  clear: () => void;
  setUnread: (unread: number) => void;
}

export const useAlertFeed = create<AlertFeed>((set) => ({
  toasts: [],
  unread: 0,
  session: 0,
  arrived: (alert, unread) =>
    set((s) =>
      // A position rule that could not compare is a note in the log, not
      // news: no toast, and it is already read.
      alert.skipped !== undefined
        ? { unread }
        : {
            // Guard against a duplicate frame (a reconnect replaying an id).
            toasts: s.toasts.some((t) => t.id === alert.id)
              ? s.toasts
              : [alert, ...s.toasts].slice(0, MAX_TOASTS),
            unread,
            session: s.toasts.some((t) => t.id === alert.id) ? s.session : s.session + 1,
          },
    ),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
  setUnread: (unread) => set({ unread }),
}));
